import type { BatchSchedule } from 'lib-shared-types-api';
import isEqual from 'lodash/isEqual';
import { useEffect } from 'react';
import { usePrevious } from 'react-use';

import { flowsApi } from 'apis/flows';

import { FlowNodeTypes } from '../components/types';
import { FlowAction } from '../types';

/**
 * A hook that fetches a list of schedules based on the nodes available in the
 * flow.
 */
export const useScheduleFetch = (
  actions: FlowAction[] | null,
): readonly BatchSchedule[] => {
  const previousActions = usePrevious(actions);

  const [readSchedules, { schedules }] =
    flowsApi.useLazyReadFlowsSchedulesBatchQuery({
      selectFromResult: ({ data }) => ({
        schedules: data?.data.schedules ?? [],
      }),
    });

  useEffect(() => {
    if (previousActions === actions) return;

    const nodeScheduleIds = getNodeSchedulesIds(actions);
    const previousNodeScheduleIds = getNodeSchedulesIds(previousActions);

    // The actions can change for many reasons, e.g. changing x-y coords of the node,
    // so we deep compare the selected schedules to avoid unnecessary API calls, and if the schedules have
    // already been fetched previously, we get them from the cache instead of making an API call.
    if (
      !!nodeScheduleIds.length &&
      !isEqual(nodeScheduleIds, previousNodeScheduleIds)
    ) {
      const preferCacheValue = true;
      readSchedules({ ids: nodeScheduleIds }, preferCacheValue);
    }
  }, [actions, previousActions, readSchedules]);

  return schedules;
};

export function getNodeSchedulesIds(actions?: FlowAction[] | null): string[] {
  if (!actions) return [];

  const set = actions.reduce<Set<string>>((acc, { nodeType, payload }) => {
    if (nodeType === FlowNodeTypes.BUSINESS_HOURS) {
      acc.add(payload.scheduleId);
    }
    return acc;
  }, new Set<string>([]));

  return Array.from(set);
}
